import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Eye, Hand, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandingSettings } from "@/pages/admin/BrandingSettings";
import { SlugEditor } from "@/pages/admin/SlugEditor";
import { api, errMsg } from "@/lib/api";

const MODES = [
  ["published", "Only published shifts (recommended)", "Staff see the rota once you publish it. Later edits stay private until you re-publish. Approved swaps and claims show straight away."],
  ["live", "Every change straight away", "Staff see shifts as soon as you add or edit them, before you publish."],
];

const CLAIMS = [
  ["approval", "Manager approves each claim", "Staff ask for an open shift and you pick who gets it on the Requests page."],
  ["instant", "First to claim gets it", "The first free team member to tap Take this shift gets it straight away. You get an email and notification."],
];

const Radios = ({ items, value, onChange, name }) => items.map(([id, label, help]) => (
  <label key={id} className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${value === id ? "border-indigo-400 bg-indigo-50" : "border-slate-200"}`} data-testid={`${name}-${id}`}>
    <input type="radio" name={name} checked={value === id} onChange={() => onChange(id)} className="mt-1 accent-indigo-600" />
    <span><span className="block text-sm font-semibold">{label}</span><span className="text-xs text-slate-500">{help}</span></span>
  </label>
));

export const StaffAppSettings = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/biz/staff-app").then((r) => setInfo(r.data));
  }, []);
  if (!info) return null;
  const url = `${window.location.origin}${info.path}`;
  const save = (path, key) => async (mode) => {
    try {
      await api.put(path, { mode });
      setInfo({ ...info, [key]: mode });
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6" data-testid="staff-app-settings">
      <div>
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold"><Smartphone className="h-5 w-5 text-indigo-600" />Staff app</h2>
        <p className="text-sm text-slate-500">Share this link or QR code with your team. They add the app to their phone and get notifications for rota changes and extra shifts.</p>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="staff-app-qr"><QRCodeSVG value={url} size={128} /></div>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="break-all rounded-lg bg-slate-50 px-3 py-2 font-mono text-sm" data-testid="staff-app-link">{url}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full" onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} data-testid="staff-app-copy-btn"><Copy className="mr-1 h-4 w-4" />Copy link</Button>
            <Button size="sm" variant="outline" className="rounded-full" asChild><a href={info.path} target="_blank" rel="noopener noreferrer" data-testid="staff-app-open-link">Open</a></Button>
            <SlugEditor slug={info.slug} onSaved={(d) => setInfo({ ...info, slug: d.slug, path: d.path })} />
          </div>
          <p className="text-xs text-slate-500" data-testid="staff-app-push-count">{info.staff_with_push} team member{info.staff_with_push === 1 ? " has" : "s have"} notifications turned on</p>
        </div>
      </div>
      <div className="space-y-2 border-t border-slate-100 pt-4">
        <p className="flex items-center gap-2 text-sm font-semibold"><Eye className="h-4 w-4 text-slate-500" />What staff can see</p>
        <Radios items={MODES} value={info.visibility} onChange={save("/biz/staff-visibility", "visibility")} name="staff-visibility" />
      </div>
      <div className="space-y-2 border-t border-slate-100 pt-4">
        <p className="flex items-center gap-2 text-sm font-semibold"><Hand className="h-4 w-4 text-slate-500" />When staff claim an open shift</p>
        <Radios items={CLAIMS} value={info.claim_mode} onChange={save("/biz/claim-mode", "claim_mode")} name="claim-mode" />
      </div>
      <BrandingSettings />
    </div>
  );
};
